import setGlobalState from '../../../redux/setGlobalState';
import store from '../../../store';
import {createEvent} from '../../../redux/account/accountActions';
import getTicketsPagePath from '../../../library/getTicketsPagePath';
import type {
    AiAgentIntent,
    AiAgentUiActionPayload,
} from '../../../redux/aiAgent/aiAgent.type';
import {
    canOpenNavigatePopup,
    getEffectiveSessionIntent,
    getEventTypeIntent,
    getFormIdForIntent,
    getIntentMeta,
    getNavigatePopupIntent,
    wantsNavigatePopup,
} from './agentSchema';

type NavigateFn = (to: string | number, options?: {state?: unknown}) => void;

export function isNewEventFormOpenInUi(): boolean {
    const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
    if(/\/e\/\d+\/event(?:\/|$)/.test(pathname)) {
        return true;
    }
    const eventTypeIntent = getEventTypeIntent();
    return !!eventTypeIntent
        && store.getState().aiAgent?.activeFormId === getFormIdForIntent(eventTypeIntent);
}

export function isOtherIntentLocked(targetIntent: AiAgentIntent): boolean {
    const locked = getEffectiveSessionIntent();
    return !!(locked && locked !== targetIntent);
}

export function lockSessionIntent(intent: AiAgentIntent): void {
    const current = store.getState().aiAgent?.sessionIntent;
    if(!current || current === intent) {
        setGlobalState('aiAgent.sessionIntent', intent);
    }
}

export function shouldSkipCreateEvent(): boolean {
    const eventTypeIntent = getEventTypeIntent();
    return !!(eventTypeIntent && isOtherIntentLocked(eventTypeIntent)) || isNewEventFormOpenInUi();
}

export async function createEventAndOpenForm(eventType: string, navigate: NavigateFn): Promise<void> {
    const eventTypeIntent = getEventTypeIntent();
    if(!eventTypeIntent || shouldSkipCreateEvent()) {
        return;
    }
    if(eventType === 'ticket' || eventType === 'area') {
        setGlobalState('ui.show.isOpenTickchakLoader', true);
    }
    setGlobalState('ui.show.isOpenCreateEvent', false);
    setGlobalState('aiAgent.isOpen', true);
    try {
        const res = await createEvent(eventType);
        if(!res?.eid) {
            throw new Error('createEvent failed');
        }
        lockSessionIntent(eventTypeIntent);
        if(res.eventType === 'scheduled') {
            setGlobalState('ui.event.isScheduled', true);
        }
        // AppComponent changeEvent resets slices + loads getEvent/permissions.
        navigate(`/e/${res.eid}/event`);
    } catch {
        setGlobalState('ui.show.isOpenTickchakLoader', false);
        throw new Error('createEvent failed');
    }
}

export async function ensureNewEventFormOpen(
    payload: AiAgentUiActionPayload | undefined,
    navigate: NavigateFn,
): Promise<void> {
    if(shouldSkipCreateEvent()) {
        return;
    }
    const eventTypeIntent = getEventTypeIntent();
    if(!eventTypeIntent) {
        return;
    }
    const meta = getIntentMeta(eventTypeIntent);
    const targetsEventForm = payload?.formId === meta?.formId
        || !!payload?.eventType
        || payload?.intent === eventTypeIntent;
    const eventType = payload?.eventType || store.getState().aiAgent?.sessionEventType;
    if(targetsEventForm && eventType) {
        await createEventAndOpenForm(eventType, navigate);
    }
}

export function openNavigatePopupIfNeeded(
    payload: AiAgentUiActionPayload | undefined,
    navigate: NavigateFn,
    eid: number | null | undefined,
): void {
    if(!wantsNavigatePopup(payload) || !eid || !canOpenNavigatePopup()) {
        return;
    }
    const popupIntent = getNavigatePopupIntent();
    if(popupIntent) {
        lockSessionIntent(popupIntent);
    }
    const {aiAgent, event, ui} = store.getState();
    const alreadyOpen = !!popupIntent && (
        aiAgent?.activeFormId === getFormIdForIntent(popupIntent)
        || (event?.type === 'subscription' && !!ui?.show?.isOpenCreateSubscription)
    );
    if(alreadyOpen) {
        if(event?.type === 'subscription') {
            setGlobalState('ui.show.isOpenCreateSubscription', true);
        }
        return;
    }
    setGlobalState('aiAgent.openTicketPopupRequestId', Date.now());
    navigate(`/e/${eid}/${getTicketsPagePath(event)}`);
}
